import { useState } from 'react'
import { ChartFrame, ChartTooltip, type TooltipState } from './Chart'
import { type Series, ceiling, exactNumber, seriesColor } from './scale'

const ROW_HEIGHT = 30
const BAR_HEIGHT = 18
const LABEL_WIDTH = 200
const VALUE_WIDTH = 62

/**
 * Magnitude across a handful of named things: the busiest devices, the rules that fired most.
 *
 * Horizontal because the labels are names — device names and rule names — and a name written
 * vertically or turned forty-five degrees is a name nobody reads. The bars grow from a single
 * baseline on the left, so their lengths are comparable at a glance, which is the entire job.
 *
 * The value rides the tip of each bar rather than an axis. There are at most ten rows, every one of
 * them is labeled, and a gridline would be ink that adds nothing.
 */
export function BarChart({
  title,
  description,
  categories,
  series,
  categoryHeading = 'Name'
}: {
  title: string
  description: string
  categories: string[]
  /** One series: this form compares things, not measures over time. */
  series: Series
  categoryHeading?: string
}) {
  const [hover, setHover] = useState<TooltipState | null>(null)

  const height = Math.max(ROW_HEIGHT, categories.length * ROW_HEIGHT) + 8
  const width = 720
  const trackLeft = LABEL_WIDTH
  const trackWidth = width - LABEL_WIDTH - VALUE_WIDTH
  const top = ceiling(Math.max(1, ...series.values))
  const color = seriesColor(0)

  return (
    <ChartFrame
      title={title}
      description={description}
      categories={categories}
      series={[series]}
      categoryHeading={categoryHeading}
      hideLegend
    >
      <div className="chart-surface">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          preserveAspectRatio="xMidYMid meet"
          className="chart-svg"
          onPointerLeave={() => setHover(null)}
        >
          {categories.map((category, index) => {
            const value = series.values[index] ?? 0
            const length = Math.max(1, (value / top) * trackWidth)
            const y = index * ROW_HEIGHT + 4

            return (
              <g
                key={category}
                onPointerEnter={() =>
                  setHover({
                    x: (trackLeft + length) / width,
                    y: (y + BAR_HEIGHT / 2) / height,
                    heading: category,
                    rows: [{ label: series.label, value, color: color }]
                  })
                }
              >
                {/* The full row is the hit target, not the bar: a two-pixel bar for a small value
                    would otherwise be almost impossible to hover. */}
                <rect x={0} y={y} width={width} height={ROW_HEIGHT} fill="transparent" />
                <text x={0} y={y + BAR_HEIGHT / 2} dy="0.32em" className="chart-tick chart-row-label">
                  {category}
                </text>
                <rect
                  x={trackLeft}
                  y={y}
                  width={length}
                  height={BAR_HEIGHT}
                  fill={color}
                  // Rounded at the data end, square against the baseline, so the bar reads as growing
                  // from the axis rather than floating beside it.
                  rx={4}
                  className="chart-bar"
                />
                <rect x={trackLeft} y={y} width={Math.min(4, length)} height={BAR_HEIGHT} fill={color} />
                <text
                  x={trackLeft + length + 8}
                  y={y + BAR_HEIGHT / 2}
                  dy="0.32em"
                  className="chart-tick chart-value-label"
                >
                  {exactNumber(value)}
                </text>
              </g>
            )
          })}
        </svg>
        <ChartTooltip state={hover} />
      </div>
    </ChartFrame>
  )
}
